"use client";

import type { Locale } from "@/lib/i18n/locales";
import { quizProgressCopy, formatQuizProgress } from "@/lib/i18n/quiz-progress-copy";

import { useEffect, useState } from "react";

import type { QuizId } from "./registry";
import {
  BUS_QUIZ_CLEAR_EVENT,
  quizStorageKeys,
  BUS_QUIZ_COMPLETION_PREFIX,
  BUS_QUIZ_PROGRESS_PREFIX,
  BUS_QUIZ_STORAGE_VERSION,
} from "./bus-quiz-storage";

type StoredProgress = {
  version: number;
  currentIndex: number;
  answers: Record<number, number>;
  checkedIds: number[];
};

type BusQuizResumeLabelProps = {
  locale?: Locale;
  persistenceRevision?: string;
  progressLocale?: Locale;
  quizId: QuizId;
  className?: string;
};

const parseProgress = (raw: string | null) => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredProgress | null;
    if (!parsed || parsed.version !== BUS_QUIZ_STORAGE_VERSION) return null;
    return parsed;
  } catch (error) {
    console.warn("Failed to parse saved quiz progress", error);
    return null;
  }
};

const hasMeaningfulProgress = (progress: StoredProgress | null) => {
  if (!progress) return false;
  const hasIndex =
    typeof progress.currentIndex === "number" && progress.currentIndex > 0;
  const hasAnswers = Object.keys(progress.answers ?? {}).length > 0;
  const hasChecked =
    Array.isArray(progress.checkedIds) && progress.checkedIds.length > 0;
  return hasIndex || hasAnswers || hasChecked;
};

export default function BusQuizResumeLabel({
  quizId,
  locale = "en",
  persistenceRevision,
  progressLocale = locale,
  className,
}: BusQuizResumeLabelProps) {
  const [resumeAt, setResumeAt] = useState<number | null>(null);

  useEffect(() => {
    const progressKey = quizStorageKeys(quizId, progressLocale, persistenceRevision).progress;
    const completionKey = quizStorageKeys(quizId, progressLocale, persistenceRevision).completion;

    const updateResume = () => {
      try {
        if (window.localStorage.getItem(completionKey)) {
          setResumeAt(null);
          return;
        }
        const progress = parseProgress(window.localStorage.getItem(progressKey));
        if (!hasMeaningfulProgress(progress)) {
          setResumeAt(null);
          return;
        }
        const index = Math.max(0, progress?.currentIndex ?? 0);
        setResumeAt(index + 1);
      } catch (error) {
        console.warn("Failed to read quiz progress", error);
        setResumeAt(null);
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (!event.key) return;
      if (
        event.key.startsWith(BUS_QUIZ_PROGRESS_PREFIX) ||
        event.key.startsWith(BUS_QUIZ_COMPLETION_PREFIX)
      ) {
        updateResume();
      }
    };

    updateResume();
    window.addEventListener("storage", handleStorage);
    window.addEventListener(BUS_QUIZ_CLEAR_EVENT, updateResume);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(BUS_QUIZ_CLEAR_EVENT, updateResume);
    };
  }, [quizId, progressLocale, persistenceRevision]);

  if (!resumeAt) return null;

  return (
    <span
      className={
        className ??
        "text-[11px] font-semibold text-slate-500 sm:text-[10px]"
      }
    >
      {formatQuizProgress(quizProgressCopy(locale).resume, {question: resumeAt})}
    </span>
  );
}
